import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Breadcrumbs, Button, Link, Typography } from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import TheaterComedyOutlinedIcon from '@mui/icons-material/TheaterComedyOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { MetaTags } from '../../components/Metatags/Metatags';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_HIGHEST,
} from './brandDashboardTokens';
import { BrandCampaignCuratedCreatorsPanel } from './BrandCampaignCuratedCreatorsPanel';
import { BrandNewUGCCampaignForm, type UgcCuratedFilterSnapshot } from './BrandNewUGCCampaignForm';
import { BrandNewContestForm } from './BrandNewContestForm';
import { BrandNewAuditionForm, type AuditionCuratedFilterSnapshot } from './BrandNewAuditionForm';
import { CAMPAIGN_TYPE_FLAGS, CampaignType, isContestCampaignEnabled } from './brandCampaignConfig';
import { useSelector } from 'react-redux';
import { Tutorial } from '../../components/Tutorial/Tutorial';
import { campaignCreationTutorialSteps } from '../../components/EsSettings/settings.data';
import { showCampaignCreationGuideSelector } from '../../selectors/selectors';

const CAMPAIGN_TYPES: { key: CampaignType; label: string; subtitle: string; Icon: typeof EmojiEventsOutlinedIcon }[] = [
    { key: 'ugc', label: 'UGC Campaign', subtitle: 'Creator partnership', Icon: CampaignOutlinedIcon },
    { key: 'contest', label: 'Contest', subtitle: 'Creative competition', Icon: EmojiEventsOutlinedIcon },
    { key: 'audition', label: 'Audition', subtitle: 'Cast & crew', Icon: TheaterComedyOutlinedIcon },
];

export default function BrandNewCampaignPage() {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const [searchParams] = useSearchParams();
    const [campaignType, setCampaignType] = useState<CampaignType>('ugc');

    useEffect(() => {
        const typeParam = searchParams.get('type');
        if (typeParam === 'audition' || typeParam === 'ugc' || typeParam === 'contest') {
            const enabled =
                typeParam === 'contest' ? isContestCampaignEnabled() : CAMPAIGN_TYPE_FLAGS[typeParam].enabled;
            if (enabled) {
                setCampaignType(typeParam);
            }
        }
    }, [searchParams]);

    const showCampaignTour = useSelector(showCampaignCreationGuideSelector);

    const [creatorInvitedIds, setCreatorInvitedIds] = useState(() => new Set<string>());
    const [curatedTalentInviteUserIds, setCuratedTalentInviteUserIds] = useState<string[]>([]);
    const [auditionCuratedSnapshot, setAuditionCuratedSnapshot] = useState<AuditionCuratedFilterSnapshot>({
        gig_type: 'cast',
        shoot_location: '',
        role_gig_types: [],
        min_age: '',
        max_age: '',
    });
    const [ugcCuratedSnapshot, setUgcCuratedSnapshot] = useState<UgcCuratedFilterSnapshot>({
        country: '',
        world_wide: false,
        category: '',
        sub_category: '',
    });

    const auditionInvitedIds = useMemo(() => new Set(curatedTalentInviteUserIds), [curatedTalentInviteUserIds]);

    useEffect(() => {
        if (campaignType !== 'audition') {
            setCuratedTalentInviteUserIds([]);
        }
    }, [campaignType]);

    const toggleCreatorInvite = useCallback((id: string) => {
        setCreatorInvitedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleCuratedTalentInvite = useCallback((userId: string) => {
        if (!userId) return;
        setCuratedTalentInviteUserIds(prev =>
            prev.includes(userId) ? prev.filter(x => x !== userId) : [...prev, userId],
        );
    }, []);

    return (
        <MetaTags title="New campaign | Brand | Thspian" description="Create a campaign">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%', pb: 2, minWidth: 0 }}>
                <Breadcrumbs
                    aria-label="breadcrumb"
                    sx={{ alignSelf: 'flex-start', '& .MuiBreadcrumbs-separator': { color: t.form_label } }}>
                    <Link
                        component={RouterLink}
                        to={ROUTES.BRAND_CAMPAIGNS}
                        underline="hover"
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            color: t.primary_white,
                            '&:hover': { color: BRAND_LUXE_PRIMARY },
                        }}>
                        Campaigns
                    </Link>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: BRAND_LUXE_PRIMARY }}>
                        New campaign
                    </Typography>
                </Breadcrumbs>

                <Box>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: '2rem', md: '2.25rem' },
                            fontWeight: 800,
                            letterSpacing: '-0.03em',
                            color: BRAND_LUXE_PRIMARY,
                            mb: 1,
                        }}>
                        New campaign
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, color: t.primary_white, fontSize: 16 }}>
                        Choose a campaign type and define your vision.
                    </Typography>
                </Box>

                {/* Segmented control */}
                <Box
                    id="campaign-type-selector"
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                        gap: 1.5,
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        p: 1,
                        borderRadius: 2,
                    }}>
                    {CAMPAIGN_TYPES.map(({ key, label, subtitle, Icon }) => {
                        const active = campaignType === key;
                        const locked = !CAMPAIGN_TYPE_FLAGS[key].enabled;
                        return (
                            <Button
                                key={key}
                                onClick={() => !locked && setCampaignType(key)}
                                disabled={locked}
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    py: 2,
                                    px: 3,
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    textAlign: 'left',
                                    justifyContent: 'flex-start',
                                    bgcolor: active ? BRAND_LUXE_PRIMARY : 'transparent',
                                    color: active ? '#392700' : t.primary_white,
                                    boxShadow: active ? '0 4px 20px rgba(255,193,7,0.25)' : 'none',
                                    transition: 'all 0.25s ease',
                                    opacity: locked ? 0.45 : 1,
                                    '&:hover': {
                                        bgcolor: active ? BRAND_LUXE_PRIMARY : BRAND_SURFACE_CONTAINER_HIGHEST,
                                    },
                                    '&.Mui-disabled': {
                                        color: 'rgba(255,255,255,0.35)',
                                        bgcolor: 'transparent',
                                    },
                                }}>
                                {locked && (
                                    <LockOutlinedIcon
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            fontSize: 16,
                                            color: 'rgba(255,255,255,0.4)',
                                        }}
                                    />
                                )}
                                <Icon
                                    sx={{
                                        fontSize: 26,
                                        color: locked
                                            ? 'rgba(255,255,255,0.3)'
                                            : active
                                            ? '#5f4200'
                                            : BRAND_LUXE_PRIMARY,
                                    }}
                                />
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 15,
                                            lineHeight: 1.2,
                                            color: 'inherit',
                                        }}>
                                        {label}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 12,
                                            color: locked
                                                ? 'rgba(255,255,255,0.25)'
                                                : active
                                                ? '#5f4200'
                                                : 'rgba(255,255,255,0.5)',
                                        }}>
                                        {subtitle}
                                    </Typography>
                                </Box>
                            </Button>
                        );
                    })}
                </Box>

                {/* 7 / 5 grid: form + curated creators */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: 'repeat(12, 1fr)' },
                        gap: { xs: 3, lg: 4 },
                        alignItems: 'start',
                        minWidth: 0,
                    }}>
                    <Box
                        sx={{
                            gridColumn: { lg: 'span 7' },
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                            minWidth: 0,
                        }}>
                        {campaignType === 'ugc' && (
                            <BrandNewUGCCampaignForm
                                themeColors={t}
                                invitedIds={creatorInvitedIds}
                                onCuratedFilterChange={setUgcCuratedSnapshot}
                            />
                        )}
                        {campaignType === 'contest' && <BrandNewContestForm themeColors={t} />}
                        {campaignType === 'audition' && (
                            <BrandNewAuditionForm
                                themeColors={t}
                                onCuratedFilterChange={setAuditionCuratedSnapshot}
                                curatedTalentInviteUserIds={curatedTalentInviteUserIds}
                            />
                        )}
                    </Box>
                    <Box
                        sx={{
                            gridColumn: { lg: 'span 5' },
                            position: { xs: 'static', lg: 'sticky' },
                            top: { lg: 24 },
                            minWidth: 0,
                        }}>
                        <BrandCampaignCuratedCreatorsPanel
                            campaignType={campaignType}
                            auditionCurated={campaignType === 'audition' ? auditionCuratedSnapshot : undefined}
                            ugcCurated={campaignType === 'ugc' ? ugcCuratedSnapshot : undefined}
                            invitedIds={campaignType === 'audition' ? auditionInvitedIds : creatorInvitedIds}
                            onToggleInvite={
                                campaignType === 'audition' ? toggleCuratedTalentInvite : toggleCreatorInvite
                            }
                        />
                    </Box>
                </Box>
            </Box>
            {showCampaignTour && (
                <Tutorial
                    stepsDetails={campaignCreationTutorialSteps}
                    configField="show_campaign_creation_guide"
                />
            )}
        </MetaTags>
    );
}
